"use client";

import React, { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import Button from "../ui/Button";

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const pathname = usePathname();

  return (
    <header className="fixed top-6 z-50 mx-auto w-full left-0 right-0 max-w-6xl pointer-events-none">
      <div className="relative flex items-center justify-end md:justify-between rounded-xl md:bg-primary-dark/80 px-4 py-2 md:py-3 md:backdrop-blur md:px-6 mx-4 pointer-events-auto">
        <Link href="/" className="hidden md:flex items-center gap-2 text-white">
          <Image
            src="/images/logo/icon.png"
            alt="AdviSify Icon"
            width={32}
            height={32}
            className="h-8 w-8 object-contain"
          />
          <Image
            src="/images/logo/logo.png"
            alt="AdviSify Logo"
            width={100}
            height={32}
            className="h-8 w-28 object-contain hidden sm:block"
          />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden gap-2 text-sm text-white/95 md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          {[
            { name: "Home", href: "/" },
            { name: "About", href: "/about" },
            { name: "Travelers", href: "/travelers" },
            { name: "Advisor", href: "/advisor" },
            { name: "Business", href: "/business" },
          ].map((item) => {
            const isActive = pathname === item.href;

            return (
              <Link
                key={item.name}
                href={item.href}
                className={`font-medium transition-all duration-0 ease-out active:scale-95 hover:scale-105 px-4 py-1.5 rounded-full border ${
                  isActive
                    ? "text-accent bg-white/10 backdrop-blur-md border-white/20 shadow-sm"
                    : "border-transparent hover:text-accent hover:bg-white/5"
                }`}
              >
                {item.name}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden md:flex gap-2">
            <Button
              variant="primary"
              size="sm"
              shape="full"
              icon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="hidden sm:block"
                >
                  <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              }
            >
              Advisor Login
            </Button>
            <Link href="/advisor">
              <Button
                variant="secondary"
                size="sm"
                shape="full"
                className="text-accent!"
              >
                Become Advisor
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button  - User request: header should be burgur menu and accssable from any where that burger menu*/}
          <div className="flex justify-end w-full md:hidden">
            <button
              className="text-white p-2.5 cursor-pointer bg-primary-dark/95 backdrop-blur-md shadow-lg rounded-xl transition-colors hover:bg-primary-dark"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                {isMenuOpen ? (
                  <>
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </>
                ) : (
                  <>
                    <line x1="3" y1="12" x2="21" y2="12"></line>
                    <line x1="3" y1="6" x2="21" y2="6"></line>
                    <line x1="3" y1="18" x2="21" y2="18"></line>
                  </>
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Navigation Dropdown */}
        {isMenuOpen && (
          <div className="absolute top-[72px] left-4 right-4 p-5 bg-primary-dark/95 backdrop-blur-md rounded-2xl shadow-2xl flex flex-col gap-6 md:hidden mx-auto border border-white/20">
            {/* Logo inside mobile menu */}
            <div className="flex justify-center border-b border-white/10 pb-4">
              <Link href="/" className="flex items-center gap-2 text-white">
                <Image
                  src="/images/logo/icon.png"
                  alt="AdviSify Icon"
                  width={32}
                  height={32}
                  className="h-8 w-8 object-contain"
                />
                <Image
                  src="/images/logo/logo.png"
                  alt="AdviSify Logo"
                  width={100}
                  height={32}
                  className="h-8 w-28 object-contain"
                />
              </Link>
            </div>

            <nav className="flex flex-col gap-2">
              {[
                { name: "Home", href: "/" },
                { name: "About", href: "/about" },
                { name: "Travelers", href: "/travelers" },
                { name: "Advisor", href: "/advisor" },
                { name: "Business", href: "/business" },
              ].map((item) => {
                const isActive = pathname === item.href;

                return (
                  <Link
                    key={item.name}
                    href={item.href}
                    className={`font-semibold py-3 my-1 rounded-xl text-center text-lg active:scale-95 transition-all duration-500 ease-out border ${
                      isActive
                        ? "text-accent bg-white/10 backdrop-blur-md border-white/20 shadow-sm"
                        : "text-white border-transparent hover:text-accent hover:bg-white/5"
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                );
              })}
            </nav>

            <div className="flex gap-3 mt-2">
              <Button
                variant="primary"
                size="sm"
                shape="full"
                className="w-full justify-center"
                icon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                }
              >
                Advisor Login
              </Button>
              <Button
                variant="secondary"
                size="sm"
                shape="full"
                className="w-full justify-center text-accent!"
              >
                Become Advisor
              </Button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
